import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState(enTranslations);

  useEffect(() => {
    // Check URL path for language
    const path = location.pathname;
    if (path.startsWith('/es/') || path === '/es') {
      setLanguage('es');
      setTranslations(esTranslations);
    } else {
      setLanguage('en');
      setTranslations(enTranslations);
    }
  }, [location.pathname]);

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    setTranslations(newLanguage === 'es' ? esTranslations : enTranslations);
    
    // Update URL using React Router
    const currentPath = location.pathname;
    const searchParams = location.search;
    
    let newPath;
    if (newLanguage === 'es') {
      // Add /es prefix
      newPath = currentPath.startsWith('/es') ? currentPath : `/es${currentPath}`;
    } else {
      // Remove /es prefix
      newPath = currentPath.startsWith('/es') ? currentPath.replace('/es', '') || '/' : currentPath;
    }
    
    navigate(newPath + searchParams);
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value === 'string') {
      // Replace parameters in the string
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] || match;
      });
    }
    
    return value || key;
  };

  const value = {
    language,
    setAppLanguage: changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
